import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
    Box, Typography, Card, CardContent, Grid, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, IconButton, Tooltip, CircularProgress, Tabs, Tab, InputAdornment,
} from "@mui/material";
import {
    Refresh, Edit, Visibility, Search, AttachMoney, CheckCircle, Pending, Cancel, DirectionsBus, Person, Phone, Email, LocationOn,
} from "@mui/icons-material";
import MainDatatable from "../../../components/datatable/MainDatatable.jsx";
import { api_urls } from "../../../utils/api-urls";

const API = api_urls + "api/admin/yatra-bookings";

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'confirmed', label: 'Confirmed', color: 'info' },
    { value: 'in_progress', label: 'In Progress', color: 'primary' },
    { value: 'completed', label: 'Completed', color: 'success' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' },
];

const PAYMENT_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'paid', label: 'Paid', color: 'success' },
    { value: 'failed', label: 'Failed', color: 'error' },
    { value: 'refunded', label: 'Refunded', color: 'default' },
];

const YatraHistory = () => {
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [updating, setUpdating] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (searchText) params.search = searchText;
            const res = await axios.get(API, { params });
            if (res.data?.success) {
                setData(res.data.data || []);
                setStats(res.data.stats || {});
            }
        } catch (e) {
            console.error('Yatra fetch error:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [statusFilter]);

    const handleSearch = () => fetchData();

    const handleSaveEdit = async () => {
        if (!editing) return;
        setUpdating(true);
        try {
            await axios.put(`${API}/${editing._id}`, {
                bookingStatus: editing.bookingStatus,
                paymentStatus: editing.paymentStatus,
                adminComment: editing.adminComment,
            });
            setEditing(null);
            fetchData();
        } catch (e) {
            alert('Update failed: ' + (e.response?.data?.message || e.message));
        } finally {
            setUpdating(false);
        }
    };

    const statCards = [
        { title: 'Total Bookings', value: stats.total || 0, icon: <DirectionsBus />, color: '#1565C0', bg: '#E3F2FD' },
        { title: 'Pending', value: stats.pending || 0, icon: <Pending />, color: '#E65100', bg: '#FFF3E0' },
        { title: 'Confirmed', value: stats.confirmed || 0, icon: <CheckCircle />, color: '#2E7D32', bg: '#E8F5E9' },
        { title: 'Completed', value: stats.completed || 0, icon: <CheckCircle />, color: '#00695C', bg: '#E0F2F1' },
        { title: 'Cancelled', value: stats.cancelled || 0, icon: <Cancel />, color: '#C62828', bg: '#FFEBEE' },
        { title: 'Total Revenue', value: '\u20B9' + (stats.totalRevenue || 0).toLocaleString(), icon: <AttachMoney />, color: '#6A1B9A', bg: '#EDE7F6' },
    ];

    const getStatusChip = (status, type = 'booking') => {
        const opts = type === 'booking' ? STATUS_OPTIONS : PAYMENT_OPTIONS;
        const found = opts.find(o => o.value === status) || { label: status || 'N/A', color: 'default' };
        return <Chip label={found.label} color={found.color} size="small" sx={{ fontWeight: 600 }} />;
    };

    const columns = [
        { name: 'S.No.', selector: (row) => data.indexOf(row) + 1, width: '70px' },
        { name: 'Name', selector: row => row?.name || 'N/A', sortable: true, wrap: true },
        { name: 'Phone', selector: row => row?.phone || 'N/A', width: '130px' },
        { name: 'Email', selector: row => row?.email || 'N/A', wrap: true },
        { name: 'Package', selector: row => row?.yatraPackageId?.title || row?.tourCode || 'N/A', wrap: true },
        { name: 'Adults/Kids', selector: row => `${row?.adults || 0}/${row?.children || 0}`, width: '110px' },
        { name: 'State / City', selector: row => `${row?.state || ''} / ${row?.city || ''}`, wrap: true },
        { name: 'Amount', selector: row => '\u20B9' + (row?.totalPrice || 0), width: '110px' },
        { name: 'Payment', selector: row => getStatusChip(row?.paymentStatus, 'payment'), width: '110px' },
        { name: 'Status', selector: row => getStatusChip(row?.bookingStatus, 'booking'), width: '120px' },
        { name: 'Date', selector: row => row?.createdAt ? moment(row.createdAt).format('DD MMM YY') : 'N/A', width: '110px' },
        {
            name: 'Actions', cell: row => (
                <Box display="flex" gap={1}>
                    <Tooltip title="View Details">
                        <IconButton size="small" color="info" onClick={() => setViewing(row)}><Visibility fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Status & Comment">
                        <IconButton size="small" color="primary" onClick={() => setEditing({ ...row })}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                </Box>
            ),
            width: '120px',
        },
    ];

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h5" fontWeight={800}>Yatra Bookings</Typography>
                    <Typography variant="body2" color="textSecondary">Manage all Yatra bookings, status & customer comments</Typography>
                </Box>
                <Button variant="contained" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>Refresh</Button>
            </Box>

            <Grid container spacing={2} mb={2}>
                {statCards.map((s, i) => (
                    <Grid item xs={6} sm={4} md={2} key={i}>
                        <Card sx={{ borderLeft: `4px solid ${s.color}` }}>
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ backgroundColor: s.bg, borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}>{s.icon}</Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">{s.title}</Typography>
                                        <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Card sx={{ mb: 2, p: 2 }}>
                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <TextField size="small" placeholder="Search name/phone/email/code..." value={searchText} onChange={e => setSearchText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        sx={{ flex: 1, minWidth: 250 }} />
                    <Button variant="outlined" onClick={handleSearch}>Search</Button>
                    <Tabs value={statusFilter} onChange={(_, v) => setStatusFilter(v)} variant="scrollable">
                        <Tab label="All" value="" />
                        {STATUS_OPTIONS.map(s => <Tab key={s.value} label={s.label} value={s.value} />)}
                    </Tabs>
                </Box>
            </Card>

            {loading ? (
                <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
            ) : (
                <Card sx={{ p: 1 }}>
                    <MainDatatable columns={columns} data={data} />
                </Card>
            )}

            <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Booking Details</DialogTitle>
                <DialogContent dividers>
                    {viewing && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}><Typography variant="h6">{viewing.yatraPackageId?.title || viewing.tourCode}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong><Person fontSize="inherit" /> Name:</strong> {viewing.name}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong><Phone fontSize="inherit" /> Phone:</strong> {viewing.phone}</Typography></Grid>
                            <Grid item xs={12}><Typography variant="body2"><strong><Email fontSize="inherit" /> Email:</strong> {viewing.email}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>Adults:</strong> {viewing.adults}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>Children:</strong> {viewing.children || 0}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong><LocationOn fontSize="inherit" /> State:</strong> {viewing.state}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>City:</strong> {viewing.city}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>Tour Code:</strong> {viewing.tourCode || '-'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>Total:</strong> \u20B9{viewing.totalPrice}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>Payment ID:</strong> {viewing.paymentId || '-'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="body2"><strong>Booked On:</strong> {moment(viewing.createdAt).format('DD MMM YYYY hh:mm A')}</Typography></Grid>
                            <Grid item xs={6}>{getStatusChip(viewing.paymentStatus, 'payment')}</Grid>
                            <Grid item xs={6}>{getStatusChip(viewing.bookingStatus, 'booking')}</Grid>
                            {viewing.spacialRequest && (
                                <Grid item xs={12}><Typography variant="body2"><strong>Special Request:</strong> {viewing.spacialRequest}</Typography></Grid>
                            )}
                            {viewing.adminComment && (
                                <Grid item xs={12}><Typography variant="body2" sx={{ p: 1.5, bgcolor: '#FFF8E1', borderRadius: 1 }}><strong>Admin Note:</strong> {viewing.adminComment}</Typography></Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewing(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Booking - {editing?.name}</DialogTitle>
                <DialogContent dividers>
                    {editing && (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField select label="Booking Status" value={editing.bookingStatus || 'pending'}
                                onChange={e => setEditing({ ...editing, bookingStatus: e.target.value })} fullWidth>
                                {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                            <TextField select label="Payment Status" value={editing.paymentStatus || 'pending'}
                                onChange={e => setEditing({ ...editing, paymentStatus: e.target.value })} fullWidth>
                                {PAYMENT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                            <TextField label="Admin Comment / Note" multiline rows={4} value={editing.adminComment || ''}
                                onChange={e => setEditing({ ...editing, adminComment: e.target.value })} fullWidth
                                placeholder="Add internal note for this booking (visible only to admins)..." />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit} disabled={updating}>
                        {updating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default YatraHistory;
